import React, { useEffect } from 'react'
import cx from 'classnames'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Frame } from '../Frame/Frame'
import { UpdateSearchButton } from '../UpdateSearchButton/UpdateSearchButton'
import { ExpiredBookingAlert } from '../ExpiredBookingAlert/ExpiredBookingAlert'
import { AvailabilitiesSection } from '../AvailabilitiesSection/AvailabilitiesSection'
import { useSetupExperienceReservationParams } from '../../hooks/useSetupExperienceReservationParams'
import { RestaurantInfo } from '../../api/restaurant/getRestaurant'
import { Ticket } from './icons/Ticket'
import { CalendarTodayIcon, GuestIcon } from '@toasttab/buffet-pui-icons'
import { useFormatCurrency } from '../../hooks/useFormatCurrency'
import { getExperiencePartySizeRange } from '../../utils/getExperiencePartySizeRange'
import { getFormattedDatesActive } from '../../utils/getFormattedDatesActive'
import { useBanquetProps, useIntlProps } from 'banquet-runtime-modules'
import { NewLineFormatter } from '../NewLineFormatter/NewLineFormatter'
import { useGetExperienceBySlug } from '../../api/experiences/useGetExperience'
import { LoadingView } from '../LoadingView/LoadingView'
import { useRestaurantContext } from '../../contexts/RestaurantContext'
import { Helmet } from 'react-helmet-async'
import { getIsPastBookable } from '../../utils/getIsPastBookable'
import { ExperienceViewImage } from './ExperienceViewImage'
import { DividerLine } from '../DividerLine/DividerLine'
import { ExperienceContentWrapper } from '../ExperienceContentWrapper/ExperienceContentWrapper'
import { Experience } from '../../api/experiences/model'
import { getExperiencePaymentDetailLabel } from './utils'

export const ExperienceView = ({
  restaurant
}: {
  restaurant: RestaurantInfo
}) => {
  const { shortUrl } = useRestaurantContext()
  const { featureFlags } = useBanquetProps()
  const navigate = useNavigate()
  const { language: locale } = useIntlProps()
  const formatCurrency = useFormatCurrency()
  const { experienceSlug } = useParams()
  const { data: experience, isLoading } = useGetExperienceBySlug(
    // We're assuming there's always an experience here, based on Router rules for this view
    { experienceSlug: experienceSlug! }
  )

  useEffect(() => {
    // Scroll to the top when the page is navigated to
    window.scrollTo(0, 0)
  }, [])

  // Redirect the user to the main reservation page
  useEffect(() => {
    // If the experience doesn't exist (because we have the FF off, or our API returned an error), don't do any redirect
    if (isLoading || !experience) {
      return
    }

    // If the experience is always available, ignore datesActive; instead, never redirect
    if (experience.alwaysAvailable) {
      return
    }

    // If the experience is exclusively available in the past, redirect
    if (getIsPastBookable(experience.datesActive)) {
      navigate(`/${shortUrl}/reserve`, {
        replace: true
      })
    }
  }, [isLoading, experience, shortUrl, navigate])

  if (!experience) {
    return <LoadingView />
  }

  const paymentDetailLabel = getExperiencePaymentDetailLabel(
    experience,
    formatCurrency
  )

  const partySizeRange = getExperiencePartySizeRange(experience)
  const formattedDateRange = getFormattedDatesActive(locale, experience)

  const hasExperienceImagesFlag = Boolean(
    featureFlags?.['nv1-experience-images']
  )
  const hasExperienceImages =
    hasExperienceImagesFlag && Boolean(experience?.imageConfig)

  return (
    <Frame
      restaurant={restaurant}
      showCoverPhoto={false}
      hasNoPadding={hasExperienceImages}
    >
      <Helmet>
        <title>
          {restaurant.name} - {experience.name} - {restaurant.city},{' '}
          {restaurant.state} | Toast Tables
        </title>
      </Helmet>
      <ExperienceViewImage experience={experience} />
      <div
        className={cx('flex flex-col gap-6', {
          'p-4 xl:p-0': hasExperienceImages
        })}
      >
        <ExpiredBookingAlert />
        <ExperienceContentWrapper
          restaurant={restaurant}
          experience={experience}
        >
          <div className='flex flex-col gap-6'>
            <div className='flex flex-col gap-2'>
              <h1 className='type-headline-4'>{experience.name}</h1>
              <p>
                By{' '}
                <Link
                  to={`/${shortUrl}/findTime`}
                  className='underline hover:no-underline'
                >
                  {restaurant.name}
                </Link>
              </p>
            </div>
            <div className='flex flex-col gap-2'>
              <UpdateSearchButton
                restaurant={restaurant}
                bookableGuid={experience.guid}
              />
            </div>
            <AvailabilitiesSection
              restaurant={restaurant}
              bookableGuid={experience.guid}
            />
            <UpdateExperienceQueryParams
              restaurant={restaurant}
              experience={experience}
            />
            <DividerLine />
            <div className='flex flex-col gap-2'>
              <h2 className='type-large font-semibold'>Details</h2>
              <ul className='flex flex-col gap-1'>
                {paymentDetailLabel && (
                  <li className='flex items-center gap-2'>
                    <Ticket width={24} height={24} />
                    From {paymentDetailLabel}
                  </li>
                )}

                <li className='flex items-center gap-2'>
                  <GuestIcon accessibility='decorative' />
                  Party size {partySizeRange}
                </li>
                <li className='flex items-center gap-2'>
                  <CalendarTodayIcon accessibility='decorative' />
                  {formattedDateRange}
                </li>
              </ul>
            </div>
            {experience.memo?.description && (
              <NewLineFormatter
                text={experience.memo.description}
                className='text-secondary'
              />
            )}
          </div>
        </ExperienceContentWrapper>
      </div>
    </Frame>
  )
}

function UpdateExperienceQueryParams({
  restaurant,
  experience
}: {
  restaurant: RestaurantInfo
  experience: Experience
}) {
  useSetupExperienceReservationParams(restaurant, experience)
  return null
}
